import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { selectUserInfo, updateUserInfo } from "@redux/slices/authSlice";
import { parseError } from "../utils/utils";
import axios from "axios";
import config from "../config";
import TeamsIntegration from "../components/TeamsIntegration";

const Settings = () => {
  const [user, setUser] = useState({ firstname: "", lastname: "", id: "", company_name: "", company_status: "" });
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const token = localStorage.getItem("accessToken");
  const dispatch = useDispatch();
  const userInfo = useSelector(selectUserInfo);
  const userRole = userInfo?.role?.toLowerCase() || "user";

  useEffect(() => {
    axios
      .get(`${config.API_BASE_URL}/api/users/me/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setUser({
          firstname: res.data.first_name,
          lastname: res.data.last_name,
          id: res.data.id,
          company_name: res.data.company_name || "N/A",
          company_status: res.data.company_status ? "Active" : "Deactivated",
        });
      })
      .catch((err) => toast.error(parseError(err)));
  }, [token]);

  const handleProfileUpdate = (e) => {
    e.preventDefault();

    // Validation
    const nameRegex = /^[A-Za-z]{2,}$/;
    if (!nameRegex.test(user.firstname)) {
      toast.error("First name must be at least 2 characters and contain only letters (no spaces).");
      return;
    }
    if (!nameRegex.test(user.lastname)) {
      toast.error("Last name must be at least 2 characters and contain only letters (no spaces).");
      return;
    }

    axios
      .put(
        `${config.API_BASE_URL}/api/users/settings/update-profile/`,
        {
          first_name: user.firstname,
          last_name: user.lastname,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((res) => {
        toast.success("Profile updated successfully");
        const updatedUser = res.data.user;
        // Sync local state with the confirmed values from the server
        setUser((prev) => ({
          ...prev,
          firstname: updatedUser.first_name,
          lastname: updatedUser.last_name,
          id: updatedUser.id,
        }));
        // Also update Redux + localStorage so Navbar & other components reflect the change
        dispatch(
          updateUserInfo({
            first_name: updatedUser.first_name,
            last_name: updatedUser.last_name,
          })
        );
      })
      .catch((err) => toast.error(parseError(err)));
  };

  const handlePasswordChange = (e) => {
    e.preventDefault();

    // Validation
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters long.");
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      toast.error("New password must contain at least one uppercase letter (A-Z).");
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      toast.error("New password must contain at least one lowercase letter (a-z).");
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      toast.error("New password must contain at least one number (0-9).");
      return;
    }
    const specialCharsMatch = newPassword.match(/[@$!%*?&]/g);
    if (!specialCharsMatch) {
      toast.error("New password must contain at least one special character (@$!%*?&).");
      return;
    }
    // No triple repeats
    if (/(.)\1\1/.test(newPassword)) {
      toast.error("New password cannot contain more than 2 consecutive identical characters.");
      return;
    }
    // No consecutive identical special characters
    if (/([@$!%*?&])\1/.test(newPassword)) {
      toast.error("Consecutive identical special characters (like @@, !!) are not allowed in new password.");
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast.error("New passwords do not match.");
      return;
    }

    axios
      .post(
        `${config.API_BASE_URL}/api/users/settings/change-password/`,
        {
          old_password: oldPassword,
          new_password: newPassword,
          confirm_new_password: confirmNewPassword,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then(() => {
        toast.success("Password changed successfully!");
        setOldPassword("");
        setNewPassword("");
        setConfirmNewPassword("");
      })
      .catch((err) =>
        toast.error(parseError(err))
      );
  };

  return (
    <div className="flex bg-white min-h-[calc(100vh-64px)] text-gray-900">
      <div className="flex-1 flex flex-col items-center justify-start px-6 pt-2 pb-6">
        <div className="w-full max-w-4xl flex flex-col gap-5">
          <div className="flex items-center justify-between mb-0 px-2">
            <div>
              <h2 className="text-3xl font-extrabold bg-gradient-to-r from-blue-700 via-purple-700 to-indigo-800 bg-clip-text text-transparent tracking-wider">
                Settings
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/integrations/listing"
                className="flex items-center justify-center bg-[#f0f7ff] text-[#1e3a8a] border border-[#cce3ff] px-5 py-2 rounded-full transition-all duration-200 font-bold shadow-sm active:scale-95 text-sm whitespace-nowrap hover:bg-[#e0efff] hover:border-[#b8d9ff]"
              >
                Integrations
              </Link>
              {userRole !== "user" && (
                <Link
                  to="/agent"
                  className="flex items-center justify-center bg-[#f0f7ff] text-[#1e3a8a] border border-[#cce3ff] px-5 py-2 rounded-full transition-all duration-200 font-bold shadow-sm active:scale-95 text-sm whitespace-nowrap hover:bg-[#e0efff] hover:border-[#b8d9ff]"
                >
                  Launch Agent
                </Link>
              )}
            </div>
          </div>
          {/* Edit Profile */}
          <form
            onSubmit={handleProfileUpdate}
            className="py-6 px-10 rounded-3xl border border-blue-400 bg-white shadow-xl shadow-blue-50/50"
          >
            <h3 className="text-2xl font-black mb-3 bg-gradient-to-r from-blue-700 via-purple-700 to-indigo-800 bg-clip-text text-transparent tracking-wider border-b border-gray-100 pb-2">
              Edit Profile
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-700 tracking-tight ml-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="John"
                  value={user.firstname}
                  onChange={(e) => setUser({ ...user, firstname: e.target.value.replace(/[^A-Za-z]/g, "") })}
                  className="w-full h-11 px-4 py-2 bg-white border border-blue-400 text-gray-900 shadow-sm placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600 transition-all text-sm"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-700 tracking-tight ml-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Doe"
                  value={user.lastname}
                  onChange={(e) => setUser({ ...user, lastname: e.target.value.replace(/[^A-Za-z]/g, "") })}
                  className="w-full h-11 px-4 py-2 bg-white border border-blue-400 text-gray-900 shadow-sm placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600 transition-all text-sm"
                />
              </div>
            </div>
            <div className="mt-6">
              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 px-6 rounded-full font-bold transition-all duration-300 shadow-lg hover:shadow-blue-200 active:scale-95 text-base tracking-wide"
              >
                Save Profile
              </button>
            </div>
          </form>

          {/* Change Password */}
          <form
            onSubmit={handlePasswordChange}
            className="py-6 px-10 rounded-3xl border border-blue-400 bg-white shadow-xl shadow-blue-50/50"
          >
            <h3 className="text-2xl font-black mb-3 bg-gradient-to-r from-blue-700 via-purple-700 to-indigo-800 bg-clip-text text-transparent tracking-wider border-b border-gray-100 pb-2">
              Change Password
            </h3>
            <div className="space-y-5">
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-700 tracking-tight ml-1">
                  Old Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  placeholder="••••••••"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value.replace(/\s/g, ""))}
                  className="w-full h-11 px-4 py-2 bg-white border border-blue-400 text-gray-900 shadow-sm placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600 transition-all text-sm"
                  required
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-700 tracking-tight ml-1">
                    New Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value.replace(/\s/g, ""))}
                    className="w-full h-11 px-4 py-2 bg-white border border-blue-400 text-gray-900 shadow-sm placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600 transition-all text-sm"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-700 tracking-tight ml-1">
                    Confirm New Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    placeholder="••••••••"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value.replace(/\s/g, ""))}
                    className="w-full h-11 px-4 py-2 bg-white border border-blue-400 text-gray-900 shadow-sm placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600 transition-all text-sm"
                    required
                  />
                </div>
              </div>
              <div className="mt-4">
                <button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 px-6 rounded-full font-bold transition-all duration-300 shadow-lg hover:shadow-blue-200 active:scale-95 text-base tracking-wide"
                >
                  Change Password
                </button>
              </div>
            </div>
          </form>

          {/* Microsoft Teams Notifications */}
          <TeamsIntegration />
        </div>
      </div>
    </div>
  );
};

export default Settings;